import { memo, useState } from 'react'
import { Box, Button, IconButton, Typography } from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import { useNavigate } from 'react-router-dom'
import { HELP_THEME } from '../../helpCenter/utils/helpConstants'
import { useHelpCenter } from '../../helpCenter/context/HelpCenterContext'
import HelpVideoCard from '../../helpCenter/components/HelpVideoCard'
import { useWellbeing } from '../context/WellbeingContext'
import { WELLBEING_SCENARIOS } from '../constants/wellbeingMessages'

const SELF_CARE_SHARE_HREF = '/resource/share/5f2f828eb31b'

const STRESS_RELIEF_VIDEOS = [
	{
		id: 'wb-water',
		title: 'Water Music',
		description: 'Gentle water sounds for a soft reset between sessions.',
		provider: 'youtube',
		embedUrl: 'https://www.youtube.com/embed/77ZozI0rw7w',
		videoId: '77ZozI0rw7w',
		duration: 'Ambient',
	},
	{
		id: 'wb-jungle-water',
		title: 'Jungle & Water',
		description: 'Nature soundscape — jungle and water together.',
		provider: 'youtube',
		embedUrl: 'https://www.youtube.com/embed/c2NmyoXBXmE',
		videoId: 'c2NmyoXBXmE',
		duration: 'Ambient',
	},
	{
		id: 'wb-wind-chimes',
		title: 'Wind Chimes',
		description: 'Soft wind chimes to settle the mind.',
		provider: 'youtube',
		embedUrl: 'https://www.youtube.com/embed/cCF6nZ1Kd8o',
		videoId: 'cCF6nZ1Kd8o',
		duration: 'Ambient',
	},
]

const ITEMS = [
	{
		id: 'breathing',
		title: 'Breathing Exercise',
		body: 'Box breathing — inhale, hold, exhale, hold. One quiet minute whenever you need it.',
		action: 'breathing',
		cta: 'Start breathing break',
	},
	{
		id: 'stress',
		title: 'Stress Relief',
		body: 'A soft reset between sessions. Pause, breathe, and return with presence.',
		action: 'stress',
		cta: 'Open calming sounds',
	},
	{
		id: 'self-care',
		title: 'Counsellor Self Care',
		body: 'Rest is part of the work. Your calm presence helps every student you meet.',
		action: 'self-care',
		cta: 'Open self-care guide',
	},
]

function HelpWellbeingPanel({ onCloseHelp }) {
	const navigate = useNavigate()
	const { openBreathingBreak } = useWellbeing()
	const { openVideo } = useHelpCenter()
	const [panel, setPanel] = useState('list')

	const handleItemClick = (item) => {
		if (item.action === 'breathing') {
			onCloseHelp?.()
			openBreathingBreak(WELLBEING_SCENARIOS.HELP)
			return
		}
		if (item.action === 'stress') {
			setPanel('stress')
			return
		}
		if (item.action === 'self-care') {
			onCloseHelp?.()
			navigate(SELF_CARE_SHARE_HREF)
		}
	}

	if (panel === 'stress') {
		return (
			<Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
					<IconButton
						size='small'
						aria-label='Back to wellbeing'
						onClick={() => setPanel('list')}
						sx={{ color: HELP_THEME.text }}
					>
						<ArrowBackRoundedIcon fontSize='small' />
					</IconButton>
					<Box>
						<Typography
							sx={{
								fontWeight: 700,
								fontSize: '1.05rem',
								color: HELP_THEME.text,
							}}
						>
							Stress Relief
						</Typography>
						<Typography sx={{ color: HELP_THEME.muted, fontSize: '0.8125rem' }}>
							Pick a calming sound — same player as Demo Videos.
						</Typography>
					</Box>
				</Box>

				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: {
							xs: '1fr',
							sm: 'repeat(2, minmax(0, 1fr))',
							md: 'repeat(3, minmax(0, 1fr))',
						},
						gap: 2,
					}}
				>
					{STRESS_RELIEF_VIDEOS.map((video) => (
						<HelpVideoCard key={video.id} video={video} onWatch={openVideo} />
					))}
				</Box>
			</Box>
		)
	}

	return (
		<Box>
			<Typography
				sx={{
					fontWeight: 700,
					fontSize: '1.05rem',
					color: HELP_THEME.text,
					mb: 0.5,
				}}
			>
				Wellbeing
			</Typography>
			<Typography sx={{ color: HELP_THEME.muted, fontSize: '0.8125rem', mb: 2 }}>
				Quiet tools for counsellor self-care — never an alert, always an invitation.
			</Typography>

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
				{ITEMS.map((item) => (
					<Box
						key={item.id}
						sx={{
							p: 2,
							borderRadius: '14px',
							border: `1px solid ${HELP_THEME.border}`,
							bgcolor: HELP_THEME.white,
							boxShadow: '0 6px 18px rgba(15, 23, 42, 0.04)',
						}}
					>
						<Typography
							sx={{ fontWeight: 700, fontSize: '0.9375rem', color: HELP_THEME.text, mb: 0.5 }}
						>
							{item.title}
						</Typography>
						<Typography
							sx={{ fontSize: '0.8125rem', color: HELP_THEME.muted, lineHeight: 1.6, mb: 1.25 }}
						>
							{item.body}
						</Typography>
						<Button
							size='small'
							variant='contained'
							onClick={() => handleItemClick(item)}
							sx={{
								textTransform: 'none',
								fontWeight: 700,
								borderRadius: '999px',
								bgcolor: '#5B4BDB',
							}}
						>
							{item.cta}
						</Button>
					</Box>
				))}
			</Box>
		</Box>
	)
}

export default memo(HelpWellbeingPanel)
